/**
 * «Финансы → P&L → Банковское обслуживание»: за что платим банкам — по видам
 * (пакет, опции, платёжки, СБП, СМС, карты, справки), по банкам и счетам,
 * штуки и цена за штуку. Клик по виду — его банки и операции; по банку —
 * его операции. Комиссия, включённая в сумму переводов и снятий, — справочно:
 * в P&L она в статье самой операции.
 *
 * Спека: `docs/specs/2026-09-30-bank-service-breakdown-design.md`.
 */
import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { EntrepreneurPicker, entrepreneurIds, withEntrepreneurs } from "@/components/EntrepreneurPicker";
import { NoAccess } from "@/components/NoAccess";
import { PNL, useCan } from "@/lib/access";
import {
  type BankFeeKind,
  type BankService,
  loadBankService,
  money,
  monthLabel,
  type PnlLine,
} from "@/lib/pnl";
import { type DirectoryRow, ENTREPRENEURS, loadDirectory } from "@/lib/statements";

const INPUT =
  "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] outline-none focus:border-border-strong";
const CELL = "whitespace-nowrap border-b border-border px-2.5 py-1 text-right tabular-nums";
const STICKY = "sticky left-0 z-10 min-w-[280px] border-b border-r border-border px-3 py-1 text-left";

/** Что выбрано для списка операций: вид и, может быть, счёт. */
type Pick = { kind: string; account_id?: number | undefined };

export function BankServicePage() {
  return useCan(PNL) ? <BankServiceScreen /> : <NoAccess what="банковскому обслуживанию" />;
}

function BankServiceScreen() {
  const [params, setParams] = useSearchParams();
  const [entrepreneurs, setEntrepreneurs] = useState<DirectoryRow[]>([]);
  const [report, setReport] = useState<BankService | null>(null);
  const [failure, setFailure] = useState("");

  const filter = useMemo(
    () => ({
      entrepreneur_id: entrepreneurIds(params),
      business: params.get("business") !== "false",
      personal: params.get("personal") !== "false",
      cash: params.get("cash") !== "false",
      date_from: params.get("date_from") ?? undefined,
      date_to: params.get("date_to") ?? undefined,
    }),
    [params],
  );
  const pick: Pick | null = params.get("kind")
    ? { kind: params.get("kind") ?? "", account_id: Number(params.get("account_id")) || undefined }
    : null;

  const setParam = (key: string, value: string | undefined) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };
  const choose = (next: Pick | null) => {
    const p = new URLSearchParams(params);
    p.delete("kind");
    p.delete("account_id");
    if (next) {
      p.set("kind", next.kind);
      if (next.account_id) p.set("account_id", String(next.account_id));
    }
    setParams(p);
  };

  useEffect(() => {
    loadDirectory(ENTREPRENEURS, true)
      .then(setEntrepreneurs)
      .catch((error: Error) => setFailure(error.message));
  }, []);

  useEffect(() => {
    setReport(null);
    setFailure("");
    loadBankService(filter)
      .then(setReport)
      .catch((error: Error) => setFailure(error.message));
  }, [filter]);

  const pnlBack = (() => {
    const next = withEntrepreneurs(new URLSearchParams(), filter.entrepreneur_id);
    if (filter.date_from) next.set("date_from", filter.date_from);
    if (filter.date_to) next.set("date_to", filter.date_to);
    return `/pnl?${next}`;
  })();

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 [&>*]:flex-none">
      <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
        <Link to={pnlBack} className="text-accent-text hover:underline">
          ← P&L
        </Link>
        <span className="font-medium">Банковское обслуживание</span>
        <EntrepreneurPicker
          entrepreneurs={entrepreneurs}
          value={filter.entrepreneur_id}
          onChange={(ids) => setParams(withEntrepreneurs(params, ids))}
        />
        <label className="flex items-center gap-1.5 text-fg-muted" title="Счета ИП">
          <input
            type="checkbox"
            checked={filter.business}
            onChange={(e) => setParam("business", e.target.checked ? undefined : "false")}
          />
          безнал ИП
        </label>
        <label className="flex items-center gap-1.5 text-fg-muted" title="Личные счета и карты">
          <input
            type="checkbox"
            checked={filter.personal}
            onChange={(e) => setParam("personal", e.target.checked ? undefined : "false")}
          />
          безнал физик
        </label>
        <span className="ml-2 text-fg-faint">период</span>
        <input
          type="date"
          className={INPUT}
          value={filter.date_from ?? ""}
          onChange={(e) => setParam("date_from", e.target.value)}
        />
        <span className="text-fg-faint">—</span>
        <input type="date" className={INPUT} value={filter.date_to ?? ""} onChange={(e) => setParam("date_to", e.target.value)} />
      </div>

      {failure ? (
        <div role="alert" className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
          {failure}
        </div>
      ) : null}

      {report === null ? (
        <div className="text-[12.5px] text-fg-muted">…</div>
      ) : report.months.length === 0 ? (
        <div className="text-[12.5px] text-fg-muted">Платежей банкам за период нет.</div>
      ) : (
        <>
          <Kinds report={report} pick={pick} onPick={choose} />
          {pick ? <Operations report={report} pick={pick} onClose={() => choose(null)} /> : null}
          {report.embedded.count > 0 ? <Embedded report={report} /> : null}
        </>
      )}
    </main>
  );
}

function Cells(props: { line: PnlLine; months: string[] }) {
  return (
    <>
      {props.months.map((m) => (
        <td key={m} className={CELL}>
          {money(props.line.values[m], true)}
        </td>
      ))}
      <td className={`${CELL} font-medium`}>{money(props.line.total, true)}</td>
    </>
  );
}

function price(row: { price_min: string | null; price_max: string | null }): string {
  if (row.price_min === null || row.price_max === null) return "";
  const [lo, hi] = [money(row.price_min), money(row.price_max)];
  return lo === hi ? lo : `${lo} – ${hi}`;
}

function Kinds(props: { report: BankService; pick: Pick | null; onPick: (pick: Pick | null) => void }) {
  const { report, pick } = props;
  const [open, setOpen] = useState<Set<string>>(() => new Set(pick ? [pick.kind] : []));
  const toggle = (kind: BankFeeKind) => {
    const next = new Set(open);
    if (next.has(kind.code)) next.delete(kind.code);
    else next.add(kind.code);
    setOpen(next);
    props.onPick(pick?.kind === kind.code && !pick.account_id ? null : { kind: kind.code });
  };
  return (
    <>
      <div className="text-[12px] text-fg-muted">
        Заплачено банкам, ₽. Вид — по назначению платежа; цена — за одну операцию списания (у каждого банка свой
        тариф). Клик по виду — банки и операции.
      </div>
      <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
        <table className="w-full border-collapse text-[12.5px]">
          <thead>
            <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
              <th className={`${STICKY} bg-bg-subtle py-2`}>вид · банк</th>
              {report.months.map((m) => (
                <th key={m} className={`${CELL} py-2 font-medium`}>
                  {monthLabel(m)}
                </th>
              ))}
              <th className={`${CELL} py-2 font-medium`}>итого</th>
              <th className={`${CELL} py-2 font-medium`}>шт.</th>
              <th className={`${CELL} py-2 font-medium`}>цена</th>
            </tr>
          </thead>
          <tbody>
            {report.kinds.map((kind) => {
              const expanded = open.has(kind.code);
              const active = pick?.kind === kind.code && !pick.account_id;
              return (
                <Fragment key={kind.code}>
                  <tr
                    className={`${kind.accounts.length ? "cursor-pointer" : ""} ${active ? "bg-accent-dim" : "hover:bg-bg-hover"}`}
                    onClick={kind.accounts.length ? () => toggle(kind) : undefined}
                  >
                    <td className={`${STICKY} ${active ? "bg-accent-dim" : "bg-bg"} font-medium`}>
                      <span className="inline-block w-4 text-fg-faint">{kind.accounts.length ? (expanded ? "▾" : "▸") : ""}</span>
                      {kind.name}
                    </td>
                    <Cells line={kind} months={report.months} />
                    <td className={CELL}>{kind.count}</td>
                    <td className={`${CELL} text-fg-muted`} title={`за одно списание; обычно — за ${kind.unit}`}>
                      {price(kind)}
                    </td>
                  </tr>
                  {expanded
                    ? kind.accounts.map((acc) => {
                        const chosen = pick?.kind === kind.code && pick.account_id === acc.account_id;
                        return (
                          <tr
                            key={acc.account_id}
                            className={`cursor-pointer text-fg-muted ${chosen ? "bg-accent-dim" : "hover:bg-bg-hover"}`}
                            onClick={() =>
                              props.onPick(chosen ? { kind: kind.code } : { kind: kind.code, account_id: acc.account_id })
                            }
                          >
                            <td className={`${STICKY} ${chosen ? "bg-accent-dim" : "bg-bg"} pl-10`}>
                              {acc.bank} <span className="text-fg-faint">· {acc.entrepreneur} · {acc.account}</span>
                            </td>
                            <Cells line={acc} months={report.months} />
                            <td className={CELL}>{acc.count}</td>
                            <td className={CELL}>{price(acc)}</td>
                          </tr>
                        );
                      })
                    : null}
                </Fragment>
              );
            })}
            <tr className="bg-bg-subtle font-semibold">
              <td className={`${STICKY} bg-bg-subtle`}>Итого — строка P&L</td>
              <Cells line={report.total} months={report.months} />
              <td className={CELL}>{report.total.count}</td>
              <td className={CELL} />
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}

function Operations(props: { report: BankService; pick: Pick; onClose: () => void }) {
  const { report, pick } = props;
  const kind = report.kinds.find((k) => k.code === pick.kind);
  const rows = report.operations.filter(
    (o) => o.kind === pick.kind && (pick.account_id === undefined || o.account_id === pick.account_id),
  );
  const account = kind?.accounts.find((a) => a.account_id === pick.account_id);
  const sum = rows.reduce((s, o) => s + Number(o.amount), 0);
  return (
    <div className="rounded-[var(--radius-card)] border border-border">
      <div className="flex items-center gap-2 border-b border-border bg-bg-subtle px-3 py-2 text-[12.5px]">
        <span className="font-medium">{kind?.name ?? pick.kind}</span>
        {account ? (
          <span className="text-fg-muted">
            · {account.bank} · {account.entrepreneur} · {account.account}
          </span>
        ) : null}
        <span className="text-fg-muted">
          · {rows.length} операций на {money(sum)} ₽
        </span>
        <button type="button" className="ml-auto text-fg-muted hover:text-fg" title="Закрыть" onClick={props.onClose}>
          ×
        </button>
      </div>
      <div className="max-h-[420px] overflow-auto">
        <table className="w-full border-collapse text-[12.5px]">
          <tbody>
            {rows.map((o) => (
              <tr key={o.id} className="hover:bg-bg-hover">
                <td className="whitespace-nowrap border-b border-border px-3 py-1 tabular-nums text-fg-muted">
                  <Link to={`/pnl/operations?id=${o.id}`} target="_blank" className="hover:underline">
                    {o.date.split("-").reverse().join(".")}
                  </Link>
                </td>
                <td className="whitespace-nowrap border-b border-border px-2.5 py-1 text-fg-muted">
                  {o.bank} · {o.entrepreneur} · {o.account}
                </td>
                <td className={CELL}>{money(o.amount)}</td>
                <td className="border-b border-border px-2.5 py-1 text-fg-muted">{o.purpose}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Embedded({ report }: { report: BankService }) {
  const { embedded, months } = report;
  return (
    <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
      <table className="w-full border-collapse text-[12.5px]">
        <tbody>
          <tr>
            <td
              colSpan={months.length + 2}
              className="border-b border-border px-3 pb-1 pt-3 text-[10.5px] font-medium uppercase tracking-[0.06em] text-fg-faint"
            >
              Справочно — комиссия внутри переводов и снятий («в сумму операции включена комиссия»). В P&L — в статье
              самой операции
            </td>
          </tr>
          <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
            <th className={`${STICKY} bg-bg-subtle py-2`}>
              {embedded.count} операций на {money(embedded.turnover, true)} ₽
            </th>
            {months.map((m) => (
              <th key={m} className={`${CELL} py-2 font-medium`}>
                {monthLabel(m)}
              </th>
            ))}
            <th className={`${CELL} py-2 font-medium`}>итого</th>
          </tr>
          <tr>
            <td className={`${STICKY} bg-bg`}>Комиссия банка</td>
            <Cells line={embedded} months={months} />
          </tr>
          {embedded.by_article.map((a) => (
            <tr key={a.article} className="text-fg-muted">
              <td className={`${STICKY} bg-bg pl-6`} colSpan={1}>
                в статье «{a.article}» <span className="text-fg-faint">· {a.count} операций</span>
              </td>
              <td colSpan={months.length} className="border-b border-border" />
              <td className={CELL}>{money(a.fee, true)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
